/**
 * Step definitions for the post-login onboarding tour.
 *
 * Uses the same driver.js popover structure as in-game RoboTutor (character
 * header + .tutorial-description) so `_driver.scss` applies identically.
 *
 * The tour has two "tracks":
 * 1. `GAME_SELECTION_STEPS`: shown on /game-selection, introduces Create / Join cards.
 * 2. `LOBBY_STEPS`: shown on /lobby/:joinCode after the user clicks "Create Game",
 *    walks through the main sections of the lobby.
 *
 * Tour visibility is driven by games_entered_count (see orchestrator).
 */

import { characterByType } from 'utils/employees'

// Duplicated from RoboTutorTutorialSteps (same markup/CSS as in-game tutor).
const createOnboardingCharacterTitle = ({ type, gender, name, role }) => `
  <div class="character-title">
    <img src="${characterByType({ type, gender, bordered: true })}" 
         class="character-image"
         style="width: 60px; height: 60px; object-fit: contain;"
         alt="Character Icon"/>
    <h2>${name}</h2>
    <p>${role}</p>
  </div>
`

// Same scroll math as scrollMainBoardToCenter; extra roots for lobby / game-selection.
const onboardingScrollOnHighlight = {
  onHighlightStarted: (element) => {
    const container =
      document.querySelector('.main-board__content') ||
      document.querySelector('.lobby__content') ||
      document.querySelector('.game-selection__shell') ||
      document.querySelector('.game-selection')
    if (container && element) {
      const containerRect = container.getBoundingClientRect()
      const elementRect = element.getBoundingClientRect()
      const elementTopInContainer = elementRect.top - containerRect.top + container.scrollTop
      const scrollTarget = elementTopInContainer - containerRect.height / 2 + elementRect.height / 2
      container.scrollTo({ top: Math.max(0, scrollTarget), behavior: 'smooth' })
    }
  }
}

const buildStep = (element, descriptionKey, t, extra = {}) => {
  const position = extra.side || 'right'
  return {
    element,
    onHighlightStarted: onboardingScrollOnHighlight.onHighlightStarted,
    popover: {
      title: createOnboardingCharacterTitle({
        type: extra.characterType || 'intro-seller',
        gender: extra.gender || 'guy',
        name: t('onboarding.character_name'),
        role: t('onboarding.character_role')
      }),
      description: `<div class="tutorial-description"><p>${t(descriptionKey)}</p></div>`,
      side: position,
      position,
      ...(extra.margin && { margin: extra.margin }),
      ...(extra.popoverClass && { popoverClass: extra.popoverClass }),
      ...(extra.align && { align: extra.align })
    }
  }
}

export const buildGameSelectionSteps = (t) => [
  buildStep(
    '.game-selection__layout',
    'onboarding.game_selection.intro.description',
    t,
    { side: 'bottom' }
  ),
  buildStep(
    '.game-selection__card--create',
    'onboarding.game_selection.create.description',
    t,
    { side: 'right' }
  ),
  buildStep(
    '.game-selection__form-group',
    'onboarding.game_selection.create_form.description',
    t,
    { side: 'right' }
  ),
  buildStep(
    '.game-selection__card--join',
    'onboarding.game_selection.join.description',
    t,
    { side: 'left' }
  ),
  buildStep(
    '.game-selection__sign-out',
    'onboarding.game_selection.logout.description',
    t,
    { side: 'left' }
  ),
  buildStep(
    '.game-selection__shell',
    'onboarding.game_selection.closing.description',
    t,
    { side: 'top' }
  )
]

const buildLobbyWelcomeStep = (t) =>
  buildStep(
    '.lobby__container',
    'onboarding.lobby.common.welcome.description',
    t,
    {
      side: 'top'
    }
  )

const buildLobbyHeaderStep = (t) =>
  buildStep(
    '.lobby__header',
    'onboarding.lobby.common.header.description',
    t,
    {
      side: 'bottom'
    }
  )

const buildLobbyCodeStep = (t) =>
  buildStep(
    '.lobby__game-code-box',
    'onboarding.lobby.common.code.description',
    t,
    {
      side: 'left'
    }
  )

const buildLobbyEditPlayerStep = (t) =>
  buildStep(
    '.lobby__current-player',
    'onboarding.lobby.host.player_panel.description',
    t,
    { side: 'right' }
  )

const buildLobbySettingsStep = (t) =>
  buildStep(
    '.lobby__settings-card',
    'onboarding.lobby.common.settings.description',
    t,
    { side: 'right' }
  )

const buildLobbySettingsJoinerStep = (t) =>
  buildStep(
    '.lobby__settings-card',
    'onboarding.lobby.joiner.settings.description',
    t,
    { side: 'right' }
  )

const buildLobbyTeamsStep = (t) =>
  buildStep(
    '.lobby__teams-section',
    'onboarding.lobby.common.teams.description',
    t,
    {
      side: 'top'
    }
  )

const buildLobbyTeamsJoinerStep = (t) =>
  buildStep(
    '.lobby__teams-section',
    'onboarding.lobby.joiner.teams.description',
    t,
    { side: 'top' }
  )

const buildLobbyFacilitatorsStep = (t) =>
  buildStep(
    '.lobby__team-slot--facilitators',
    'onboarding.lobby.common.facilitators.description',
    t,
    { side: 'left' }
  )

const buildLobbyPlayerPanelStep = (t) =>
  buildStep(
    '.lobby__current-player',
    'onboarding.lobby.joiner.player_panel.description',
    t,
    { side: 'right' }
  )

const buildLobbyStatusStep = (t) =>
  buildStep(
    '.lobby__status-section',
    'onboarding.lobby.common.status.description',
    t,
    { side: 'top' }
  )

const buildLobbyActionsStep = (t) =>
  buildStep(
    '.lobby__actions-right',
    'onboarding.lobby.common.actions.description',
    t,
    { side: 'left' }
  )

const buildLobbyClosingStep = (t) =>
  buildStep(
    '.lobby__container',
    'onboarding.lobby.common.closing.description',
    t,
    { side: 'top' }
  )

export const buildLobbySteps = (t, { isCreator }) => {
  if (isCreator) {
    return [
      buildLobbyWelcomeStep(t),
      buildLobbyHeaderStep(t),
      buildLobbyCodeStep(t),
      buildLobbyEditPlayerStep(t),
      buildLobbySettingsStep(t),
      buildLobbyTeamsStep(t),
      buildLobbyFacilitatorsStep(t),
      buildLobbyStatusStep(t),
      buildLobbyActionsStep(t),
      buildLobbyClosingStep(t)
    ]
  }

  return [
    buildLobbyWelcomeStep(t),
    buildLobbyHeaderStep(t),
    buildLobbyCodeStep(t),
    buildLobbySettingsJoinerStep(t),
    buildLobbyTeamsJoinerStep(t),
    buildLobbyFacilitatorsStep(t),
    buildLobbyPlayerPanelStep(t),
    buildLobbyStatusStep(t),
    buildLobbyActionsStep(t),
    buildLobbyClosingStep(t)
  ]
}
